import React, { useState, useEffect } from 'react';
import { rgba, darken } from 'polished';
import styled, { useTheme, keyframes } from 'styled-components';
import scrollTo from 'gatsby-plugin-smoothscroll';

import { Anchor, Button, Flex, Text } from '../../../components/blocks';

import OnlineView from '../../../images/icon/online-view.svg';
import Mail from '../../../images/icon/mail.svg';

type Props = {
  startY?: number;
  endY?: number | null;
  active?: boolean;
  green?: boolean;
  inquirytitle?: string;
  onlinetitle?: string;
};

const Explanation = styled.div<{ green?: boolean }>`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  top: -14px;
  width: fit-content;
  padding: ${({ theme }) => `${theme.spaces.s} ${theme.spaces.m}`};
  background-color: ${({ theme, green }) =>
    green
      ? darken(0.08, theme.backgroundColors.green)
      : darken(0.15, theme.fontColors.yellow)};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme, green }) =>
    green ? theme.fontColors.white : theme.fontColors.white};
  border-radius: ${({ theme }) => theme.radii.rounded};
  box-shadow: ${({ theme, green }) =>
    green ? theme.boxShadow.base : theme.boxShadow.base};

  :after {
    position: absolute;
    bottom: -16px;
    content: '';
    border: 8px solid transparent;
    border-top: 8px solid
      ${({ theme, green }) =>
        green
          ? darken(0.08, theme.backgroundColors.green)
          : darken(0.15, theme.fontColors.yellow)};
  }
`;

const SlideIn = keyframes`
  0% {
    transform: translateY(100vh);
  }
  100% {
    transform: translateY(0vh);
  }
`;

const BottomButton = styled(Button)`
  flex: 1;
  padding-left: ${({ theme }) => theme.spaces.xs};
  padding-right: ${({ theme }) => theme.spaces.xs};
`;

const BottomAnchor = styled(Anchor)`
  flex: 1;
  padding-left: ${({ theme }) => theme.spaces.xs};
  padding-right: ${({ theme }) => theme.spaces.xs};
`;

const Wrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spaces.s};
  width: 100%;
  max-width: ${({ theme }) => theme.windowSizes.s};
  margin: auto;
`;

const Root = styled.div<Props>`
  position: fixed;
  bottom: 0;
  width: 100%;
  padding: ${({ theme }) => `${theme.spaces.l} ${theme.spaces.s}`};
  background-color: ${({ theme }) =>
    rgba(theme.backgroundColors.jetblack, 0.2)};
  backdrop-filter: blur(2px);
  transform: translateY(100vh);

  &.active {
    animation: ${SlideIn} 1s ease-in-out 1;
    animation-fill-mode: forwards;
    animation-delay: 0.5s;
  }
`;

export const CVButtonBottom: React.FC<Props> = (props) => {
  const theme = useTheme();

  // スクロール位置を管理するstate
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  // 特定の範囲内にスクロールしたかどうかを管理するstate
  const [isInScrollRange, setIsInScrollRange] = useState<boolean>(false);
  // closeボタンがクリックされたかを管理するstate
  const [isClosed, setIsClosed] = useState<boolean>(false);

  // スクロールイベントのリスナーを追加
  useEffect(() => {
    const handleScroll = () => {
      // closeボタンがクリックされたら何もしない
      if (isClosed) {
        return;
      }

      // 現在のスクロール位置を取得
      const currentPosition = window.scrollY;

      // 特定の範囲内にスクロールしたら状態を変更
      if (currentPosition >= props.startY && currentPosition <= props.endY) {
        setIsInScrollRange(true);
      } else {
        setIsInScrollRange(false);
      }

      // スクロール位置をstateに更新
      setScrollPosition(currentPosition);
    };

    // スクロールイベントをリスナーに追加
    window.addEventListener('scroll', handleScroll);

    // コンポーネントがアンマウントされるときにイベントリスナーを削除
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [props.endY, isClosed]); // isClosedを依存配列に追加

  // closeボタンがクリックされたときの処理
  const handleCloseClick = () => {
    setIsClosed(true);
  };

  return (
    <>
      <Root className={isInScrollRange && !isClosed && 'active'}>
        <Wrapper>
          <BottomAnchor
            wide="head"
            color="green"
            id="footerInquiryButton"
            fill
            radius="rounded"
            shadow="nav"
            to="#inquiry"
            // onClick={() => scrollTo('#inquiry')}
          >
            <Explanation green>
              {props.inquirytitle ? props.inquirytitle : '3分でDentisがわかる'}
            </Explanation>
            <Flex gap={theme.spaces.s}>
              <Mail width="24px" height="24px" alt="ダウンロード" />
              <Text as="span" size="s" fontWeight="bold" color="white">
                資料請求・お問い合わせ
              </Text>
            </Flex>
          </BottomAnchor>
          <BottomAnchor
            wide="head"
            color="yellow"
            id="footerDemoButton"
            fill
            radius="rounded"
            shadow="nav"
            to="/form/demo"
          >
            <Explanation>
              {props.onlinetitle ? props.onlinetitle : 'オンラインデモ実施中'}
            </Explanation>
            <Flex gap={theme.spaces.s}>
              <OnlineView width="24px" height="24px" alt="ダウンロード" />
              <Text as="span" size="s" fontWeight="bold" color="brown">
                無料オンラインデモ
              </Text>
            </Flex>
          </BottomAnchor>
        </Wrapper>
      </Root>
    </>
  );
};
